/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-30 16:41:43
 * @ Modified by: David Helmick
 * @ Modified time: 2024-08-02 12:33:14
 * @ Description: Page component for adding a shared portfolio to the user's orgs.
 */

import React, { useState } from 'react'

import {
  Grid,
  Button,
  Card,
  CardContent,
  Typography,
  Checkbox,
  List,
  ListItem,
  Backdrop,
  CircularProgress,
  Box,
} from '@mui/material'

import {
  pageShellWrapperSx,
  pageShellMainSx,
  pageShellContentSx,
  pageShellInnerSx,
} from '@/layout-blueprints/chromeSx'

// Centralized lodash imports - much cleaner!
import {
  includes,
  remove,
  map,
  find,
  compact,
  isEmpty,
  get,
  merge,
  cloneDeep,
  filter,
} from '@/utils/lodash'

import housefoliosLogo from '@/assets/images/HousefoliosCloudTransparent.png'
import { useMutation } from '@apollo/client/react'

import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import withSession from '@/housefolios-components/Session/withSession'
import { ADD_SHARED_PORTFOLIO } from '../mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { enqueueSnackbar } from 'notistack'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import useAffiliate from '@/hooks/useAffiliate'
import { useNavigate } from 'react-router'

function AddSharedPortfolioComponent(props) {
  const [selected, setSelected] = useState(
    compact(
      map(props.session.me.roles, (roleOrg) => {
        if (
          roleOrg.organization &&
          roleOrg.role[0] !== 'deactive' &&
          roleOrg.organization._id !== 'superAdmin'
        )
          return roleOrg.organization._id
      }),
    ),
  )
  const [loader, setLoader] = React.useState(false)

  const [addSharedPortfolio] = useMutation(ADD_SHARED_PORTFOLIO)

  const urlParams = new URLSearchParams(window.location.search)
  let token = urlParams.get('sharedPortfolioToken')

  const navigate = useNavigate()
  if (!token) navigate('/')

  const preferences = merge(
    cloneDeep(DEFAULT_PREFERENCES),
    props?.session?.me?.activeOrg?.memberPreferences,
    props?.session?.me?.activeOrg?.preferences,
    props.session.me.preferences,
  )

  const toggleOrg = (orgId) => {
    let newSelected = [...selected]
    if (includes(newSelected, orgId))
      remove(newSelected, (select) => select === orgId)
    else newSelected.push(orgId)
    setSelected(newSelected)
  }

  const [{ affiliate, affiliateLoading, urlAffiliate }] =
    useAffiliate({})

  const selectAll = () => {
    if (
      selected.length <
      filter(
        props.session.me.roles,
        (roleOrg) =>
          roleOrg.organization &&
          roleOrg.role[0] !== 'deactive' &&
          roleOrg.organization._id !== 'superAdmin',
      ).length
    )
      setSelected(
        compact(
          map(props.session.me.roles, (roleOrg) => {
            if (
              roleOrg.organization &&
              roleOrg.role[0] !== 'deactive' &&
              roleOrg.organization._id !== 'superAdmin'
            )
              return roleOrg.organization._id
          }),
        ),
      )
    else setSelected([])
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    setLoader(true)

    if (props.token) token = props.token

    for (let i = 0; i < selected.length; i++) {
      await addSharedPortfolio({
        variables: {
          token,
          orgId: selected[i],
        },
        refetchQueries: [{ query: GET_ME }],
      })
        .then(async ({ data  }) => {
          setLoader(false)
          enqueueSnackbar(
            `Added shared portfolio to org ${
              find(
                props.session.me.roles,
                (role) => role.organization._id === selected[i],
              ).organization.name
            }`,
            {
              variant: 'success',
              autoHideDuration: 3000,
            },
          )
          //props.history.push(routes.PROPERTIES + 'card')
        })
        .catch((error) => {
          setLoader(false)
          const message = error.message
          if (includes(message, 'expired'))
            enqueueSnackbar('Token has expired', {
              variant: 'error',
              autoHideDuration: 3000,
            })
          else if (includes(message, 'user'))
            enqueueSnackbar('Token intended for a different user', {
              variant: 'error',
              autoHideDuration: 3000,
            })
          else if (includes(message, 'Org'))
            enqueueSnackbar('Org owns this portfolio', {
              variant: 'error',
              autoHideDuration: 3000,
            })
          else if (includes(message, 'Portfolio'))
            enqueueSnackbar('Portfolio already added', {
              variant: 'error',
              autoHideDuration: 3000,
            })
          else
            enqueueSnackbar('Token is invalid', {
              variant: 'error',
              autoHideDuration: 3000,
            })
          console.log(error)
        })
    }

    if (props.handleClose) props.handleClose()
    else
      navigate(
        get(preferences, 'general.defaultPage.value') || '/launchpad',
      )
  }

  if (affiliateLoading)
    return (
      <Backdrop open={true} style={{ color: '#fff', zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )

  return (
    <Box sx={props.handleClose ? undefined : pageShellWrapperSx}>
      <Box sx={props.handleClose ? undefined : pageShellMainSx}>
        <Box sx={pageShellContentSx}>
          <Box sx={pageShellInnerSx}>
            <Box
              sx={{
                flexGrow: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={[{ py: '3rem' }, { position: "relative", zIndex: "7", width: "100%" }]}>
                <Card>
                  <CardContent>
                    <Grid container direction="column" spacing={4}>
                      <Grid>
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Grid>
                            <Grid container direction="column">
                              <Grid>
                                <Typography
                                  variant="h6"
                                  style={{ fontWeight: 600 }}
                                >
                                  Add Shared Portfolio
                                </Typography>
                              </Grid>
                              <Grid>
                                <Typography>
                                  Choose the organizations to add the
                                  shared portfolio to
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid>
                            <img
                              src={
                                urlAffiliate
                                  ? get(
                                      affiliate,
                                      'sidebar.sidebarMemberSmallImage.0.url',
                                    ) || housefoliosLogo
                                  : housefoliosLogo
                              }
                              alt="Housefolios"
                              style={{ width: 60, height: 50 }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* <Grid>
                        <Button
                          variant='outlined'
                          fullWidth={true}
                        >
                          <FontAwesomeIcon
                            style={{ marginRight: 10 }}
                            icon={['fab', 'google']}
                            size="1x"
                          />
                          <Typography>SIGN IN WITH GOOGLE</Typography>
                        </Button>
                      </Grid>
                      <Grid style={{ textAlign: 'center' }}>
                        <Typography>OR</Typography>
                      </Grid> */}
                      <Grid>
                        <BlockUi
                          blocking={loader}
                          loader={
                            <PacmanLoader
                              color={'var(--primary)'}
                              loading={loader}
                            />
                          }
                          message={
                            <Box sx={{ color: '#368cff' }}>
                              Adding Shared Portfolio
                            </Box>
                          }
                        >
                          <Grid container direction="row" spacing={2}>
                            <Grid size={12}>
                              <Box
                                style={{ width: '100%' }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Box sx={[{
                                  height: "50px !important",
                                  width: "50px !important",
                                  lineHeight: "50px !important",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  p: 0,
                                  mr: "1rem",
                                }, {
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: "0.25rem",
                                  position: "relative",
                                  fontSize: "1.2rem",
                                  transition: "all 0.2s ease-in-out",
                                  "&:last-child": { mr: 0, marginRight: 0 },
                                  "& .badge": { position: "absolute", right: 0, bottom: 0 },
                                  "& svg, & .svg-inline--fa": { display: "block" },
                                  marginRight: "0.25rem",
                                }]}>
                                  <Checkbox
                                    checked={
                                      selected.length ===
                                      filter(
                                        props.session.me.roles,
                                        (roleOrg) =>
                                          roleOrg.organization &&
                                          roleOrg.role[0] !==
                                            'deactive' &&
                                          roleOrg.organization._id !==
                                            'superAdmin',
                                      ).length
                                    }
                                    indeterminate={
                                      !isEmpty(selected) &&
                                      selected.length <
                                        filter(
                                          props.session.me.roles,
                                          (roleOrg) =>
                                            roleOrg.organization &&
                                            roleOrg.role[0] !==
                                              'deactive' &&
                                            roleOrg.organization
                                              ._id !== 'superAdmin',
                                        ).length
                                    }
                                    //onChange={() => toggleOrg(roleOrg.organization._id)}
                                    onClick={selectAll}
                                  />
                                </Box>
                                <div>
                                  <Box sx={[{ fontWeight: 700 }, {
                                    fontSize: "0.875rem !important",
                                    color: "#070919",
                                    "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                                  }]}>
                                    Select All
                                  </Box>
                                  {/* <div>
                                    <div style={{ backgroundColor: '#797d95' }}>Completed</div>
                                    <div style={{ color: '#797d95' }}>Not Selected</div>
                                  </div> */}
                                </div>
                                <div style={{ flex: 1 }} />
                                <div>
                                  {/* <Chip color="primary" label="Share marketplace" 
                                      // onClick={(e) => {
                                      //   toggleOrg(roleOrg.organization._id)
                                      // }}
                                  /> */}
                                </div>
                              </Box>
                            </Grid>
                            <Grid size={12}>
                              <List
                                component="div"
                                sx={{
                                  paddingTop: 0,
                                  paddingBottom: 0,
                                  "& .MuiListItem-root, & .MuiListItemButton-root": {
                                    width: "100%",
                                    padding: "0.75rem 1.25rem",
                                    backgroundColor: "#fff",
                                    border: "1px solid rgba(0, 0, 0, 0.125)",
                                    borderRadius: 0,
                                    margin: 0,
                                    borderWidth: "0 0 1px",
                                    "&.MuiListItem-button": {
                                      borderRadius: 0,
                                      borderWidth: "0 0 1px",
                                      "& .MuiTouchRipple-root": { opacity: 0.2 },
                                    },
                                    "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
                                    "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
                                    "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
                                    "&:first-of-type": { borderTop: "0 !important" },
                                    "&:last-child": { borderBottom: "0 !important" },
                                  },
                                }}
                                style={{
                                  height: 400,
                                  overflow: 'scroll',
                                }}
                              >
                                {map(
                                  props.session.me.roles,
                                  (roleOrg) => {
                                    if (
                                      roleOrg.organization &&
                                      roleOrg.role[0] !==
                                        'deactive' &&
                                      roleOrg.organization._id !==
                                        'superAdmin'
                                    ) {
                                      return (
                                        <ListItem
                                          component="a"
                                          button
                                          href={null}
                                          disableRipple
                                          onClick={(e) => {
                                            toggleOrg(
                                              roleOrg.organization
                                                ._id,
                                            )
                                          }}
                                         
                                          sx={{
                                            transition: "all 0.2s ease-in-out !important",
                                            transform: "scale(1) !important",
                                            boxShadow: "none",
                                            position: "relative",
                                            zIndex: "0",
                                            "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                                            display: "flex",
                                            backgroundColor: "#fff",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Box
                                            style={{ width: '100%' }}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Box sx={[{
                                              height: "50px !important",
                                              width: "50px !important",
                                              lineHeight: "50px !important",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              p: 0,
                                              mr: "1rem",
                                            }, {
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              mr: "0.25rem",
                                              position: "relative",
                                              fontSize: "1.2rem",
                                              transition: "all 0.2s ease-in-out",
                                              "&:last-child": { mr: 0, marginRight: 0 },
                                              "& .badge": { position: "absolute", right: 0, bottom: 0 },
                                              "& svg, & .svg-inline--fa": { display: "block" },
                                              marginRight: "0.25rem",
                                            }]}>
                                              <Checkbox
                                                style={{
                                                  marginRight: 8,
                                                }}
                                                checked={includes(
                                                  selected,
                                                  roleOrg.organization
                                                    ._id,
                                                )}
                                                //onChange={() => toggleOrg(roleOrg.organization._id)}
                                              />
                                            </Box>
                                            <div>
                                              <Box sx={[{ fontWeight: 700, }, {
                                                fontSize: "0.875rem !important",
                                                color: "#070919",
                                                "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                                              }]}>
                                                {
                                                  roleOrg.organization
                                                    .name
                                                }
                                              </Box>
                                              {/* <div>
                                              <div style={{ backgroundColor: '#797d95' }}>Completed</div>
                                              <div style={{ color: '#797d95' }}>Not Selected</div>
                                            </div> */}
                                            </div>
                                            <div
                                              style={{ flex: 1 }}
                                            />
                                            {/* <div>
                                            <Chip color="primary" label="Share portfolio" 
                                                // onClick={(e) => {
                                                //   toggleOrg(roleOrg.organization._id)
                                                // }}
                                            />
                                          </div> */}
                                          </Box>
                                        </ListItem>
                                      )
                                    }
                                  },
                                )}
                              </List>
                            </Grid>
                            <Grid size={12}>
                              <Button
                                variant="contained"
                                type="submit"
                                // color='primary'
                                fullWidth={true}
                                onClick={onSubmit}
                                style={{
                                  backgroundColor: '#04a3e3',
                                  color: 'white',
                                }}
                                disabled={isEmpty(selected)}
                              >
                                <Typography>Add Portfolio</Typography>
                              </Button>
                            </Grid>
                            <Grid size={12}>
                              <Button
                                variant="contained"
                                fullWidth={true}
                                onClick={
                                  props.handleClose
                                    ? props.handleClose
                                    : () =>
                                        navigate(
                                          get(
                                            preferences,
                                            'general.defaultPage.value',
                                          ) || '/launchpad',
                                        )
                                }
                              >
                                <Typography>
                                  {props.handleClose
                                    ? 'Close'
                                    : 'Continue to Default Page'}
                                </Typography>
                              </Button>
                            </Grid>
                          </Grid>
                        </BlockUi>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default withSession(AddSharedPortfolioComponent)
